<script lang="ts">
    import * as Sentry from '@sentry/browser';
    import { get } from 'svelte/store';
    import { afterUpdate, onMount } from 'svelte';
    import { _ } from 'svelte-i18n';
    import { z } from 'zod';

    import { explodeAndOr } from '../telematics/components/FilterByParameters/FilterManager';
    import { TacticalPlanRuleApi } from '../TacticalPlan/TacticalPlanRuleApi';
    import type { TacticalPlanRule } from '../TacticalPlan/TacticalPlanRule';
    import ButtonSmallBlack from '../../shared/ButtonSmallBlack.svelte';
    import { debug, tacticalPlanStore, planning } from '../../../stores/store';
    import type { MapType, MapUpdateDataType } from '../Maps/types';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import ZoneOneWithoutZone from './ZoneOneWithoutZone.svelte';
    import type { VehicleResponseType } from '../Vehicles/types';
    import IconExpandAll from '../../shared/Icons/IconExpandAll.svelte';
    import { ZoneDataGenerator } from './ZoneDataGenerator';
    import { onMapReady } from '../../utilities/onMapReady';
    import { VehiclesApi } from '../Vehicles/VehiclesApi';
    import InputText from '../../shared/InputText.svelte';
    import { api } from '../../../stores/api';
    import { ZoneEditor } from './ZoneEditor';
    import type { ZoneType } from './types';
    import { MapApi } from '../Maps/MapApi';
    import ZoneOne from './ZoneOne.svelte';
    import { ZoneApi } from './ZoneApi';
    import Sortable from '../../utilities/sortable';
    import { sortZonesByNewOrder } from '../TacticalPlan/sortZonesByNewOrder';
    import { type ZoneListConflictType, type ZoneListErrorType } from './ZoneListTypes';
    import type { ValidatorReport } from '../TacticalPlan/ValidatorReport';
    import { closeModal, openModal } from 'svelte-modals';
    import ModalZoneFromCoordsParams from './ModalZoneFromCoordsParams.svelte';
    import type { ZoneFromCoordsParams } from './ModalZoneFromCoordsParams.svelte';
    import ZoneFromCoordsMapBar from './ZoneFromCoordsMapBar.svelte';
    import { features } from '../../shared/Features/features';
    import ModalError from '../../shared/Modals/ModalError.svelte';
    import ModalWait from '../../shared/Modals/ModalWait.svelte';

    export let mode: 'full' | 'simple' = 'full';
    export let readOnly: boolean = false;
    export let rule: TacticalPlanRule | undefined = undefined;
    export let alreadyUsedVehicles: number[] = [];
    export let updateRule: (rule: TacticalPlanRule) => void = (rule: TacticalPlanRule) => {};
    export let zones: ZoneType[] = [];
    export let saved: boolean = true;
    export let loading: boolean = true;
    export let tacticalPlanValidatorReport: ValidatorReport;
    export let zoneEditor: ZoneEditor | null = null;

    let mapId: number | null;
    let map: MapType;
    let errors: {[k: string]: [ZoneListErrorType]} = {};
    let error: string = '';
    let conflictsMessage: string = '';
    let conflictsHeader = '';
    let vehicles: VehicleResponseType[] = [];
    let saving = false;
    let limitVisibleItems: number = 4;
    let showOnlyLimitVisibleItems: boolean = true;
    let sortable: any;
    let sortableList: HTMLDivElement;
    let showJobsForDay: string | null;
    let zoneFromCoordsParams: ZoneFromCoordsParams = {vehicleIds: [], limit: 100, radius: 100, distanceFrom: 'nearest', day: get(planning).selectedDay, multi: false};
    let zoneFromCoordsDebugNumbers: any[] = [];
    let zoneFromCoordsMapBar: ZoneFromCoordsMapBar|null = null;
    let dirty: boolean = false;
    let prevReadOnlyState: boolean|null = null;

    let onZoneRemove = function (evt: Event) {
        let zone_id = (<CustomEvent>evt).detail.zone_id;
        let index = zones.findIndex(z => {
            return z.id == zone_id;
        });
        if (zoneEditor && index >= 0) {
            let affectedPolygons = zoneEditor.zoneGetNeighbouringPolygons(zone_id);
            zones.splice(index, 1);
            zones = zones;
            zoneEditor.zoneRemove(zone_id);
            updateZoneErrors(affectedPolygons);
            saved = false;
            dirty = true;
            zoneFromCoordsRemoveDebugNumbers();
        }
    };

    let onZoneAdd = function (evt: Event) {
        if (zoneEditor) {
            let alreadyExistsNames = zones.filter(z => z.name !== null).map(z => z.name || '');
            let newZone = (<CustomEvent>evt).detail.zone;
            if (!newZone.name) {
                newZone.name = ZoneDataGenerator.generateName(alreadyExistsNames, $_('Zone'));
            }
            zones.push(newZone);
            zoneEditor.zoneChange(newZone, true);
            updateZoneErrors(zoneEditor.zoneGetNeighbouringPolygons(newZone.id));
            saved = false;
            dirty = true;
            zones = zones;
            zoneFromCoordsRemoveDebugNumbers();
        }
    };

    let onZoneAddFromCoords = async function (evt: Event) {
        if (!zoneFromCoordsMapBar) {
            let props: any = Object.assign(zoneFromCoordsParams || {}, {
                alreadyUsedVehicles,
                allowUseAlreadyUsedVehicle: false,
                allowToRemoveAssignment: false,
                onProceed: async (params: ZoneFromCoordsParams) => {
                    zoneFromCoordsParams = params;
                    let requestResponse = await zoneFromCoordsRequest((<CustomEvent>evt).detail.coords);
                    if (requestResponse?.status == 'ok' && requestResponse?.zones?.length > 0) {
                        if (!zoneFromCoordsParams.multi) {
                            zoneFromCoordsParams.vehicleIds = [0];
                            zoneFromCoordsCreateBar();
                        }
                        return true;
                    } else {
                        return $_('zone_could_not_be_created-'+(requestResponse.message || 'calculation-error'));
                    }
                }
            });
            props.limit = props.limit.toString();
            props.radius = props.radius.toString();
            openModal(ModalZoneFromCoordsParams, props);
        } else {
            openModal(ModalWait, {});
            let requestResponse = await zoneFromCoordsRequest((<CustomEvent>evt).detail.coords);
            closeModal();
            if (requestResponse?.status != 'ok' || requestResponse?.zones?.length == 0) {
                openModal(ModalError, {
                    message: $_('zone_could_not_be_created-'+(requestResponse.message || 'calculation-error'))
                });
            } else {
                zoneFromCoordsParams.vehicleIds = [0];
            }
        }
    }

    let zoneFromCoordsRequest = async function(coords: any) {
        let requestParams = {
            map_id: map.id,
            vehicle_ids: zoneFromCoordsParams.vehicleIds,
            coords: {type: 'Point', coordinates: [coords.x, coords.y]},
            day: zoneFromCoordsParams.day,
            zones: zones,
            limit: zoneFromCoordsParams.limit,
            radius: zoneFromCoordsParams.radius,
            distance_from: zoneFromCoordsParams.distanceFrom
        };
        let response = await ZoneApi.createFromCoords(get(api), requestParams);
        if (zoneEditor && response?.status == 'ok' && response?.zones?.length > 0) {
            zoneFromCoordsRemoveDebugNumbers();
            let jobFilter: any = {};
            if (rule && Object.keys(explodeAndOr(rule.getRule().details.filter).queries).length) {
                jobFilter = rule.getRule().details.filter;
            }
            jobFilter.target_date = showJobsForDay = zoneFromCoordsParams.day;
            zoneEditor.setJobFilter(jobFilter);
            for (let zone of response.zones) {
                zoneEditor.polygonAdd(zone.geojson, zone.vehicle_id > 0 ? zone.vehicle_id : null);
                if (zone.stack && get(debug)) {
                    for (let i = 0; i < zone.stack.length; i++) {
                        let c = zone.stack[i].coordinates;
                        let o = new Targeo.MapObject.Oval({x: c[0], y: c[1]}, 10, null, {zIndex: 600, strokeWeight: 1.5, strokeColor: '#ffffff', fillColor: '#000000', label: {text: i.toString(), size: 10, color: '#fff', offset: {x: 0, y: 0}}});
                        zoneFromCoordsDebugNumbers.push(o);
                        Targeo.Base.Map.UOAdd(o);
                    }
                }
            }
        }
        return response;
    }

    let zoneFromCoordsCreateBar = function() {
        let mapBarElement = document.createElement('div');
        mapBarElement.style.cssText = 'width: 100%; height: 34px; background-color: rgba(255, 255, 255, 0.9);';
        let shadow = mapBarElement.attachShadow({mode: 'open'});
        Targeo.Base.Map.modules.Controls.addControl(
            'zone-from-coords-bar',
            mapBarElement,
            {
                align: 'bottom-left',
                edge: true,
                visibilityPriority: 1000,
                attractPriority: 1000,
            }
        );
        zoneFromCoordsMapBar = new ZoneFromCoordsMapBar({
            target: shadow,
            props: {
                params: zoneFromCoordsParams,
                alreadyUsedVehicles,
                onClose: () => {
                    Targeo.Base.Map.modules.Controls.removeControl('zone-from-coords-bar');
                    zoneFromCoordsMapBar = null;
                }
            }
        });
    }

    let zoneFromCoordsRemoveDebugNumbers = function() {
        for (let i = 0; i < zoneFromCoordsDebugNumbers.length; i++) {
            Targeo.Base.Map.UORemove(zoneFromCoordsDebugNumbers[i].id);
        }
        zoneFromCoordsDebugNumbers = [];
    }

    let updateZoneErrors = function (polygons: any[]|null|undefined = null) {
        if (zoneEditor) {
            let errorsOnUpdate: ZoneListErrorType[] = [];
            let checkedZones = polygons ? polygons.map(p => p._zone) : zones.map(z => z.id);
            let conflicts: ZoneListConflictType[] = zoneEditor.checkValidity(polygons);
            console.log('addZone, conflicts', polygons, conflicts);
            if (conflicts.length) {
                errorsOnUpdate = parseConflicts(conflicts);
            }
            for (let i = 0; i < checkedZones.length; i++) {
                if (errors[checkedZones[i]]) {
                    for (let j = 0; j < errors[checkedZones[i]].length; j++) {
                        let affectedZone = errors[checkedZones[i]][j]?.details?.zoneId;
                        if (affectedZone && (affectedZone in errors) && !checkedZones.includes(affectedZone)) {
                            checkedZones.push(affectedZone);
                            errorsOnUpdate = errorsOnUpdate.concat(
                                parseConflicts(
                                    zoneEditor.checkValidity([zoneEditor.zoneGetPolygon(affectedZone)])
                                )
                            );
                        }
                    }
                    delete(errors[checkedZones[i]]);
                }
            }
            for (let i = 0; i < errorsOnUpdate.length; i++) {
                if (!(errorsOnUpdate[i].zoneId in errors)) {
                    errors[errorsOnUpdate[i].zoneId] = [];
                }
                errors[errorsOnUpdate[i].zoneId].push(errorsOnUpdate[i]);
            }
            for (let i in errors) {
                if (zones.filter(z => z.id == i).length == 0) {
                    delete(errors[i]);
                }
            }
            console.log('addZone, errors', errorsOnUpdate, errors, Object.values(errors).flat());
            if (rule) {
                rule.setErrors(Object.values(errors).flat());
                updateRule(rule);
                rule = rule;
            }
        }
    };

    let onZonesGeomChange = function (evt: Event) {
        if (zoneEditor) {
            let polygons = [];
            for (let i = 0, ii = zones.length; i < ii; i++) {
                if (zones[i].id in (<CustomEvent>evt).detail.zones) {
                    zones[i].geom = (<CustomEvent>evt).detail.zones[zones[i].id];
                    polygons.push(zoneEditor.zoneGetPolygon(zones[i].id));
                }
            }
            updateZoneErrors(polygons);
            zones = zones;
            zoneFromCoordsRemoveDebugNumbers();
        }
    };

    let onZoneChange = function (evt: Event) {
        if (zoneEditor) {
            zoneEditor.zoneChange((<CustomEvent>evt).detail.zone);
            updateZoneErrors([zoneEditor.zoneGetPolygon((<CustomEvent>evt).detail.zone.id)]);
        }
    };

    let onStateChange = function (evt: Event) {
        saved = !(<CustomEvent>evt).detail.dirty || !zones.length;
        dirty = !saved;
    };

    let getMapData = function (orderByOnly: boolean = false): MapUpdateDataType {
        let data: MapUpdateDataType;
        data = {
            properties: {
                name: map.name,
            },
            zones: [],
        };
        let zonesFromEditor = zoneEditor.getZones();
        for (let i = 0, ii = zones.length; i < ii; i++) {
            let zone = {
                // ZoneType but without map_id and ..
                id: typeof zones[i].id === 'number' ? zones[i].id : null, // TODO: check what means starting _ in zone id
                name: zones[i].name || '',
                color: zones[i].color,
                vehicle_id: typeof zones[i].vehicle_id === 'number' ? zones[i].vehicle_id : null,
                order_by: zones[i].order_by,
                geom: zones[i].geom
            };
            let zoneFromEditor = zonesFromEditor.filter(z => z.id == zone.id)[0];
            if (zoneFromEditor && zoneFromEditor.attributes) {
                zone.attributes = zoneFromEditor.attributes;
            }
            if (orderByOnly) {
                zone = {
                    order_by: zones[i].order_by,
                    id: typeof zones[i].id === 'number' ? zones[i].id : null, // TODO: check what means starting _ in zone id
                };
            }
            data.zones.push(zone);
            // data.zones.push({
            //     id: typeof zones[i].id === 'number' ? zones[i].id : null, // TODO: check what means starting _ in zone id
            //     name: zones[i].name || '',
            //     color: zones[i].color,
            //     geom: zones[i].geom,
            //     vehicle_id: typeof zones[i].vehicle_id === 'number' ? zones[i].vehicle_id : null,
            //     order_by: zones[i].order_by,
            // });
            if (zones[i].vehicle_id !== null) {
                alreadyUsedVehicles.push(zones[i].vehicle_id as number);
            }
        }
        return data;
    };

    let parseConflicts = function (conflicts: ZoneListConflictType[]): ZoneListErrorType[] {
        let errors: ZoneListErrorType[] = [];
        for (let i = 0; i < conflicts.length; i++) {
            if (conflicts[i].zone && conflicts[i].conflicts.errors.length) {
                for (let j = 0; j < conflicts[i].conflicts.errors.length; j++) {
                    let vehicle =
                        typeof conflicts[i].zone.vehicle_id === 'number'
                            ? vehicles.filter(v => v.id == conflicts[i].zone.vehicle_id)[0]
                            : null;
                    let zoneName: string|null = conflicts[i].zone.name || null;
                    if (zoneName === null) {
                        zoneName = vehicle ? $_('with-vehicle') + ' ' + vehicle.name : $_('zone-without-vehicle')
                    }
                    errors.push({
                        uniqueId: [
                            conflicts[i].conflicts.errors[j].type,
                            conflicts[i].zone.id && conflicts[i].conflicts.errors[j].zoneId ? Math.min(conflicts[i].zone.id ?? 0, conflicts[i].conflicts.errors[j].zoneId ?? 0) : (conflicts[i].zone.id ?? ''),
                            conflicts[i].zone.id && conflicts[i].conflicts.errors[j].zoneId ? Math.max(conflicts[i].zone.id ?? 0, conflicts[i].conflicts.errors[j].zoneId ?? 0) : ''
                        ].filter((s: string|number) => s).join('_'),
                        description: $_('zone-editor-conflict-' + conflicts[i].conflicts.errors[j].type, {
                            values: { zone: zoneName }
                        }),
                        type: conflicts[i].conflicts.errors[j].type,
                        zoneId: conflicts[i].zone.id !== null  ? conflicts[i].zone.id.toString() : null,
                        details: conflicts[i].conflicts.errors[j],
                        timestamp: (new Date()).getTime()
                    });
                }
            }
        }
        return errors;
    };

    let mapSave = async function (created: boolean, orderByOnly: boolean = false) {
        zoneFromCoordsRemoveDebugNumbers();
        if (zoneEditor && (!saving || created)) {
            saving = true;
            loading = true;
            updateZoneErrors();
            conflictsHeader = '';
            conflictsMessage = '';
            if (map.id > 0) {
                try {
                    let data = getMapData(orderByOnly);
                    await MapApi.updateZones(get(api), map.id, data);
                    saved = true;
                    dirty = false;
                    zones = await ZoneApi.getAllByMapId(get(api), map.id);
                    if (zoneEditor) {
                        zoneEditor.init(zones);
                        if (map.id != mapId) {
                            if (rule && rule.getId() !== null) {
                                rule.setMapId(map.id);
                                // updateRule(rule);
                                await TacticalPlanRuleApi.updateOne(
                                    get(api),
                                    rule.getId() as number,
                                    rule.getRuleReadyToRequest()
                                );
                                if (
                                    rule?.getInnerId() ==
                                    $tacticalPlanStore.triggerStartSaveThisRuleByInnerId
                                ) {
                                    $tacticalPlanStore.triggerStartSaveThisRuleByInnerId = '';
                                }
                            }
                            mapId = map.id;
                        } else {
                            if (
                                rule?.getInnerId() ==
                                $tacticalPlanStore.triggerStartSaveThisRuleByInnerId
                            ) {
                                $tacticalPlanStore.triggerStartSaveThisRuleByInnerId = '';
                            }
                        }
                    }
                } catch (err) {
                    console.error('map save err', err);
                    if (typeof err === 'string') error = err;
                } finally {
                    loading = false;
                    saving = false;
                    $tacticalPlanStore.triggerStartSaveThisRuleByInnerId = '';
                }
            } else {
                mapSaveCopy();
            }
        }
    };

    let mapSaveCopy = function () {
        loading = true;
        saving = true;
        let data = getMapData();
        if (map.id > 0) {
            data.properties.name += ' (kopia)';
        }
        MapApi.create(get(api), data.properties)
            .then(response => {
                map.id = response.id;
                map.name = response.name;
                for (let i = 0, ii = zones.length; i < ii; i++) {
                    zones[i].id = null;
                }
                mapSave(true);
            })
            .catch(err => {
                console.error('map create', err);
            });
    };

    let mapDiscardChanges = async function() {
        if (zoneEditor && !saving) {
            loading = true;
            conflictsHeader = '';
            conflictsMessage = '';
            if (map.id > 0) {
                saved = true;
                dirty = false;
                zones = await ZoneApi.getAllByMapId(get(api), map.id);
                if (zoneEditor) {
                    zoneEditor.init(zones);
                }
                loading = false;
            }
            updateZoneErrors();
        }
    }

    function reloadData() {
        if (typeof rule !== 'undefined' && rule.getMapId() !== null) {
            mapId = rule.getMapId();
        } else {
            mapId = null;
        }
        loading = true;
        Promise.all([
            mapId
                ? MapApi.getOne(get(api), mapId)
                : MapApi.create(get(api), {name: ''}),
            mapId ? ZoneApi.getAllByMapId(get(api), mapId) : Promise.resolve([]),
            VehiclesApi.getAll(get(api)),
            onMapReady(),
        ])
            .then(response => {
                map = response[0];
                if (!mapId) {
                    mapId = map.id;
                    if (rule) {
                        rule.setMapId(map.id);
                        updateRule(rule);
                    }
                }
                zones = response[1];
                vehicles = response[2] ?? [];
                if (loading) {
                    if (!zoneEditor) {
                        zoneEditor = new ZoneEditor({
                            mapId: mapId,
                            translateFunc: $_,
                            editable: !readOnly,
                            filterQueries: rule ? rule.getRule().details.filter : {},
                            createZoneFromCoords: $features.feature_tetris,
                            vehicles: vehicles,
                            jobDateRange: $tacticalPlanStore.volume.dateRange,
                            grid: $features.feature_micro_grid ? {} : false
                        });
                        zoneEditor.addEventListener('zoneRemove', onZoneRemove);
                        zoneEditor.addEventListener('zoneAdd', onZoneAdd);
                        zoneEditor.addEventListener('zonesChange', onZonesGeomChange);
                        zoneEditor.addEventListener('stateChange', onStateChange);
                        zoneEditor.addEventListener('zoneAddFromCoords', onZoneAddFromCoords);
                        zoneEditor.addEventListener('onBusy', (evt) => {
                            if (evt.detail.busy) {
                                openModal(ModalWait, {});
                            } else {
                                closeModal();
                            }
                        });
                    }
                    zoneEditor.init(zones);
                    updateZoneErrors();
                }
            })
            .catch(err => {
                console.error('ZoneList, error', err);
                error = err;
            })
            .finally(() => {
                loading = false;
            });
    }

    function onChangeVehicle(vehicle_id: number | null) {
        rule?.setVehicleId(vehicle_id);
        if (rule) updateRule(rule);
    }

    async function switchEditorState() {
        if (zoneEditor) {
            if (rule) {
                let jobFilter = {};
                if (Object.keys(explodeAndOr(rule.getRule().details.filter).queries).length) {
                    jobFilter = rule.getRule().details.filter;
                }
                if (showJobsForDay) {
                    jobFilter.target_date = showJobsForDay;
                }
                zoneEditor.setJobFilter(jobFilter);
            }
            if (readOnly) {
                showJobsForDay = null;
            } else {
                await MapApi.prepareZonesForEdit(get(api), map.id);
                await zoneEditor.gridSetVolume($tacticalPlanStore.volume);
            }
            zoneEditor.setEditable(!readOnly);
            if (readOnly) {
                if (!saved) {
                    mapSave(false);
                } else if (dirty) {
                    dirty = false;
                    mapDiscardChanges();
                }
            }
        }
    }

    onMount(() => {
        rule?.getErrors().forEach((err) => {
            if (err.zoneId) {
                errors[err.zoneId] = err;
            }
        });

        reloadData();

        return () => {
            zoneFromCoordsRemoveDebugNumbers();
            loading = false;
            if (zoneEditor) {
                zoneEditor.destroy();
                zoneEditor = null;
            } else {
                Sentry.captureMessage('captureMessage: ZoneEditor not found in ZoneList.svelte');
            }
            zones = [];
            error = '';
        };
    });

    let zonesOrder: number[] = [];

    afterUpdate(() => {
        if (sortableList !== null && !readOnly) {
            // console.log('sortable, init', rule?.getId(), readOnly);
            sortable = Sortable.create(sortableList, {
                // group: { put: true }, // https://sortablejs.github.io/Sortable/#sorting-disabled
                // sort: !readOnly,
                handle: '.sortable-item-handle-rule',
                animation: 350,
                // fallbackOnBody: true,
                onEnd: function () {
                    if (readOnly) {
                        console.log('sortable ZoneList, readOnly');
                        return;
                    }
                    console.log('sortable ZoneList');
                    zonesOrder = sortable
                        .toArray('data-id')
                        .map((v: string) => parseInt(v) ?? null)
                        .filter((v: number | null) => v !== null);
                    try {
                        zones = sortZonesByNewOrder(zones, zonesOrder);
                        if (zoneEditor) {
                            zoneEditor.init(zones);
                        }
                        mapSave(false, true);
                    } catch (e) {
                        console.warn('sortable error', e);
                    }
                },
            });
        }
    });

    // $: {
    //     if (sortable) {
    //         console.log('sortable.option', rule?.getId(), readOnly);
    //         sortable.option('disabled', readOnly);
    //     }
    // }

    $: {
        mapId = mapId;
        readOnly = readOnly;
        // console.log('ZoneList, mapId', mapId, readOnly);
        if (zoneEditor) {
            if (readOnly !== prevReadOnlyState) {
                switchEditorState();
            }
        }
        prevReadOnlyState = readOnly;
    }

    $: {
        showOnlyLimitVisibleItems = readOnly;
        if (zones.length - 2 <= limitVisibleItems) showOnlyLimitVisibleItems = false;
    }

    $: {
        if ($tacticalPlanStore.triggerZonesSave) {
            console.log('triggerZonesSave', $tacticalPlanStore.triggerZonesSave);
            $tacticalPlanStore.triggerZonesSave = false;
            if (saved) {
                $tacticalPlanStore.triggerStartSaveThisRuleByInnerId = '';
            } else {
                mapSave(false);
            }
        }
    }

    $: {
        if (zoneFromCoordsMapBar) {
            zoneFromCoordsMapBar.$set({alreadyUsedVehicles});
        }
    }
</script>

<div class:loading>
    {#if error}
        <MessageDanger bind:error />
    {:else}
        {#if $debug}
            mapId:{mapId} saved:{saved} triggerZonesSave:{$tacticalPlanStore.triggerZonesSave}
        {/if}
        {#if mode === 'full'}
            <div style="margin-bottom:20px;">
                {$_('Map name')}:
                <InputText bind:value={map.name} />
            </div>
        {/if}
        {#if conflictsMessage}
            <MessageDanger
                bind:error={conflictsMessage}
                bind:header={conflictsHeader}
                bind:message={conflictsMessage}
                footer={false}
                hideButton={false}
            />
        {/if}
        {#if zones.toSorted((a, b) => (a.order_by > b.order_by ? 1 : -1)) && vehicles}
            <div
                style="display: flex; flex-direction: column; row-gap: 5px;"
                bind:this={sortableList}
            >
                {#each zones as zone, index (zone.id ?? index)}
                    {#if zoneEditor && rule && (index <= limitVisibleItems - 1 || !showOnlyLimitVisibleItems)}
                        <div data-id={zone.id} class={!readOnly ? 'sortable-item-handle-rule' : ''}>
                            {#if $debug}
                                {zone.order_by} / {zone.id} / {index}
                            {/if}
                            <ZoneOne
                                bind:rule
                                {zone}
                                {vehicles}
                                bind:alreadyUsedVehicles
                                bind:readOnly
                                on:remove={onZoneRemove}
                                on:change={onZoneChange}
                                tacticalPlanValidatorReport={tacticalPlanValidatorReport}
                                {zoneEditor}
                            />
                        </div>
                    {/if}
                {/each}
                {#if zones.length > limitVisibleItems && showOnlyLimitVisibleItems}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div
                        class="unfold-zones gradient-parent"
                        on:click={() => {
                            showOnlyLimitVisibleItems = false;
                        }}
                        title={$_('Show all zones') + ` (${zones.length})`}
                    >
                        <div class="gradient-back"></div>
                        <div
                            style="display: flex; flex-direction: row; align-items: center; justify-content: flex-end;"
                        >
                            {zones.length}
                            <IconExpandAll
                                scale={0.5}
                                title={$_('Show all zones') + ` (${zones.length})`}
                            />
                        </div>
                    </div>
                {:else if rule && zones.length > 0}
                    <ZoneOneWithoutZone
                        vehicle_id={rule.getVehicleId()}
                        {rule}
                        {vehicles}
                        bind:alreadyUsedVehicles
                        bind:readOnly
                        {onChangeVehicle}
                    />
                {/if}
            </div>
            {#if mode === 'full'}
                <div style="margin-top:20px;">
                    {#if !saved}
                        <ButtonSmallBlack
                            text={$_('Save')}
                            showIcon={false}
                            on:click={() => {
                                mapSave(false);
                            }}
                            bind:disabled={saved}
                        />
                    {/if}
                    {#if zones.length > 0 && mode === 'full'}
                        <ButtonSmallBlack
                            text={$_('Save as copy')}
                            showIcon={false}
                            on:click={mapSaveCopy}
                            bind:disabled={saved}
                        />
                    {/if}
                </div>
            {/if}
        {/if}
    {/if}
</div>

<style>
    .unfold-zones {
        display: flex;
        justify-content: flex-end;
        /*margin-right: 15px;*/
        font-size: var(--list-element-data-font-size);
        column-gap: 4px;
        cursor: pointer;
    }
    .loading {
        opacity: 0.5;
        filter: grayscale(100%);
        pointer-events: none;
    }
    .gradient-parent {
        position: relative;
    }
    .gradient-back {
        position: absolute;
        bottom: 18px;
        /* left:0px; */
        width: 100%;
        height: 70px;
        background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.9) 100%
        ); /* W3C */
    }
</style>
